import React, { useState, useCallback } from "react";
import "./action-chip.scss";

export interface ActionChipProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  label: string;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  backgroundColor?: string;
  textColor?: string;
  size?: "small" | "medium";
  active?: boolean;
  defaultActive?: boolean;
  activeIcon?: React.ReactNode;
  activeBackgroundColor?: string;
  activeTextColor?: string;
  onActiveChange?: (active: boolean, id?: string) => void;
}

const ActionChip = ({
  label,
  icon,
  iconPosition = "left",
  backgroundColor,
  textColor,
  size = "small",
  active,
  defaultActive = false,
  activeIcon,
  activeBackgroundColor,
  activeTextColor,
  onActiveChange,
  className,
  style,
  disabled,
  type = "button",
  onClick,
  ...rest
}: ActionChipProps) => {
  const isControlled = active !== undefined;
  const [internalActive, setInternalActive] = useState(defaultActive);
  const isActive = isControlled ? active : internalActive;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const next = !isActive;
      if (!isControlled) setInternalActive(next);
      onActiveChange?.(next, rest.id);
      onClick?.(e);
    },
    [isActive, isControlled, onActiveChange, onClick, rest.id],
  );

  const cls = "action-chip";

  const rootClass = [
    cls,
    size === "medium" ? `${cls}--medium` : "",
    isActive ? `${cls}--active` : "",
    disabled ? `${cls}--disabled` : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const resolvedBg = isActive && activeBackgroundColor ? activeBackgroundColor : backgroundColor;
  const resolvedColor = isActive && activeTextColor ? activeTextColor : textColor;

  const rootStyle: React.CSSProperties = {
    ...(resolvedBg ? { backgroundColor: resolvedBg } : {}),
    ...(resolvedColor ? { color: resolvedColor } : {}),
    ...style,
  };

  const hasStyle = Object.keys(rootStyle).length > 0;

  const resolvedIcon = isActive && activeIcon != null ? activeIcon : icon;

  const iconEl = resolvedIcon != null && (
    <span className={`${cls}__icon`} aria-hidden="true">
      {resolvedIcon}
    </span>
  );

  return (
    <button
      className={rootClass}
      style={hasStyle ? rootStyle : undefined}
      disabled={disabled}
      type={type}
      onClick={handleClick}
      aria-pressed={activeIcon != null || activeBackgroundColor != null ? isActive : undefined}
      {...rest}
    >
      {iconPosition === "left" && iconEl}
      <span className={`${cls}__label`}>{label}</span>
      {iconPosition === "right" && iconEl}
    </button>
  );
};

export { ActionChip };
export default ActionChip;
